import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import { ChevronDownIcon } from "components/common/Icons";

import styles from "./styles.module.scss";

export type ReviewFilter = "all" | "positive" | "negative";

interface FilterOption {
    value: ReviewFilter;
    labelKey: string;
}

const FILTER_OPTIONS: FilterOption[] = [
    { value: "all", labelKey: "reviews.filterAll" },
    { value: "positive", labelKey: "reviews.filterPositive" },
    { value: "negative", labelKey: "reviews.filterNegative" },
];

interface ReviewsFilterProps {
    value: ReviewFilter;
    onChange: (value: ReviewFilter) => void;
}

export default function ReviewsFilter({ value, onChange }: ReviewsFilterProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    const selectedLabel = FILTER_OPTIONS.find((o) => o.value === value);

    const handleToggle = () => setOpen((prev) => !prev);

    const handleSelect = useCallback(
        (filterValue: ReviewFilter) => {
            onChange(filterValue);
            setOpen(false);
        },
        [onChange],
    );

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className={styles.filter} ref={ref}>
            <button
                type="button"
                className={cn(styles.filter__trigger, { [styles["filter__trigger--open"]]: open })}
                onClick={handleToggle}
            >
                <span className={styles.filter__label}>
                    {selectedLabel ? t(selectedLabel.labelKey) : ""}
                </span>
                <ChevronDownIcon
                    className={cn(styles.filter__chevron, {
                        [styles["filter__chevron--open"]]: open,
                    })}
                />
            </button>
            {open && (
                <ul className={styles.filter__dropdown}>
                    {FILTER_OPTIONS.map((option) => (
                        <li key={option.value}>
                            <button
                                type="button"
                                className={cn(styles.filter__option, {
                                    [styles["filter__option--active"]]: option.value === value,
                                })}
                                onClick={() => handleSelect(option.value)}
                            >
                                {t(option.labelKey)}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
